import React, { Suspense, lazy, useEffect } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { LayoutSplashScreen } from "../../components/SplashScreen";
import AuthFooter from "../../components/AuthFooter";
import { useSelector } from "react-redux";
import AuthHeader from "../../components/AuthHeader";
import useUnit from "../../utils/useUnit";
import { canAccessAnyArea, canAccessArea } from "../../utils/PermissionUtils";

const DashboardPage = lazy(() => import("./dashboard/DashboardPage"));
const AcessosPage = lazy(() => import("./acessos/AcessosPage"));
const ConfiguracoesPage = lazy(() => import("./configuracoes/ConfiguracoesPage"));
const PesquisasPage = lazy(() => import("./pesquisas/PesquisasPage"));
const PlanosDeAcaoPage = lazy(() => import("./planosDeAcao/PlanosDeAcaoPage"));
const DocsPage = lazy(() => import("./docs/DocsPage"));
const TermosDeUso = lazy(() => import("./configuracoes/termos/TermosDeUsoIndex"));
const PAYMENT_PAGE = "/configuracoes/dados-de-pagamento";

// Pagamento pendente: só a área de pagamento fica disponível, para a empresa conseguir se regularizar.
function RestrictedHomePage({ canManageBilling }) {
    return (
        <Suspense fallback={<LayoutSplashScreen />}>
            <div className="container">
                <AuthHeader />

                <div className="routeContainer d-flex flex-column gap-24">
                    <div className="billing-restricted-alert" role="alert">
                        <span className="bold">O acesso da sua empresa à Lumi está restrito por pendência de pagamento.</span> {canManageBilling ? "Atualize o cartão de crédito ou pague a fatura em aberto abaixo para liberar o acesso." : "Procure o administrador da conta da sua empresa para regularizar o pagamento."}
                    </div>

                    {canManageBilling && (
                        <Switch>
                            <Route path="/configuracoes" component={ConfiguracoesPage} />
                            <Route exact path="/termos-de-uso" component={TermosDeUso} />
                            <Redirect to={PAYMENT_PAGE} />
                        </Switch>
                    )}
                </div>

                <AuthFooter />
            </div>
        </Suspense>
    );
}

export default function HomePage() {
    let routes = [];

    const { user } = useSelector((state) => state.auth);
    const location = useLocation();

    const { selectedUnit, readOnly } = useUnit();

    useEffect(() => {
        window.scrollTo(0, 0);
        routes = [];
    }, [location.pathname]);

    if (user?.ctm_status === "WAITING_FOR_PAYMENT") {
        return <RestrictedHomePage canManageBilling={canAccessArea(user, "dadosDePagamento")} />;
    }

    if (canAccessAnyArea(user, ["dashboard", "engajamento"])) {
        routes.push(<Route key="/dashboard" path="/dashboard" component={DashboardPage} />);
    }

    if (canAccessAnyArea(user, ["usuarios", "equipes", "gruposDeUsuario"])) {
        routes.push(<Route key="/acessos" path="/acessos" component={AcessosPage} />);
    }

    if (canAccessArea(user, "equipes")) {
        routes.push(<Redirect key="/team" exact from="/team" to="/acessos/equipes" />);
    }

    if (canAccessArea(user, "pesquisas")) {
        routes.push(<Route key="/Pesquisas" path="/Pesquisas" component={PesquisasPage} />);
    }

    if (canAccessArea(user, "documentos")) {
        routes.push(<Route key="/docs" path="/docs" component={DocsPage} />);
    }

    if (canAccessArea(user, "planosDeAcao")) {
        routes.push(<Route key="/planos-de-acao" path="/planos-de-acao" component={PlanosDeAcaoPage} />);
    }

    if (canAccessAnyArea(user, ["empresa", "dadosDePagamento"])) {
        routes.push(<Route key="/configuracoes" path="/configuracoes" component={ConfiguracoesPage} />);
    }

    const defaultPath = routes.find((route) => route.props.path)?.props.path;

    routes.push(<Redirect key="/engajamento" exact from="/engajamento" to="/dashboard/telepsicologia" />);

    routes.push(<Route key="/termos-de-uso" exact path="/termos-de-uso" component={TermosDeUso} />);

    routes.push(<Redirect key="redirect-default" to={defaultPath ?? "/404"} />);

    return (
        <Suspense fallback={<LayoutSplashScreen />}>
            <div className="container">
                <AuthHeader />

                <div className="routeContainer">
                    <Switch key={readOnly ? selectedUnit.id : "propria"}>{routes.map((route) => route)}</Switch>
                </div>

                <AuthFooter />
            </div>
        </Suspense>
    );
}
